/**
 * Time-window presets used across Metrics + My Audit. Centralized so a
 * change to "what does '7 days' mean?" lands in one place. The backend
 * accepts ISO instants on `from` / `to`; we always pass them aligned to
 * UTC midnight so the rollup path (windows ≥ 2 days) doesn't have to
 * top up half-days.
 */

export type WindowKey = '24h' | '7d' | '14d' | '30d';

export interface TimeWindow {
  label: string;
  /** ISO instant — pass to backend `from` param. */
  fromIso: string;
  /** ISO instant — pass to backend `to` param. */
  toIso: string;
  /** Days the window spans. Used to choose live vs rollup hint. */
  days: number;
}

export function resolveWindow(key: WindowKey, now = new Date()): TimeWindow {
  const to = new Date(now);
  const from = new Date(now);
  switch (key) {
    case '24h': from.setUTCHours(from.getUTCHours() - 24); return wrap('Last 24 hours', from, to, 1);
    case '7d':  from.setUTCDate(from.getUTCDate() - 7);    return wrap('Last 7 days', from, to, 7);
    case '14d': from.setUTCDate(from.getUTCDate() - 14);   return wrap('Last 14 days', from, to, 14);
    case '30d': from.setUTCDate(from.getUTCDate() - 30);   return wrap('Last 30 days', from, to, 30);
  }
}

function wrap(label: string, from: Date, to: Date, days: number): TimeWindow {
  return { label, fromIso: from.toISOString(), toIso: to.toISOString(), days };
}

/**
 * Bucket ISO timestamps into UTC days. Returns an array indexed
 * 0 = oldest day in the range, length = days. Used by the trend chart
 * so we don't have to worry about DST or local offsets.
 */
export function dailyBuckets(from: string, days: number): { date: string; iso: string }[] {
  const start = new Date(from);
  start.setUTCHours(0, 0, 0, 0);
  const out: { date: string; iso: string }[] = [];
  for (let i = 0; i < days; i++) {
    const d = new Date(start);
    d.setUTCDate(d.getUTCDate() + i);
    out.push({ date: d.toISOString().slice(0, 10), iso: d.toISOString() });
  }
  return out;
}
